import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { createAsyncStorage } from '@/lib/asyncStorage'
import { chromeStorage } from '@/lib/storage'

export interface Task {
  id: string
  title: string
  category: 'general' | 'study' | 'work'
  priority: 'low' | 'medium' | 'high'
  date: string // YYYY-MM-DD format
  done: boolean
  recurrence: 'none' | 'daily' | 'weekly' | 'monthly' | 'custom'
  recurringDays?: number[] // 0-6 for weekly (0=Saturday for Jalali)
  recurringEndDate?: string // YYYY-MM-DD format
  createdAt: number
  updatedAt: number
}

export interface TasksState {
  tasks: Task[]
  addTask: (task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'> & { id?: string }) => void
  updateTask: (id: string, updates: Partial<Task>) => void
  deleteTask: (id: string) => void
  toggleTask: (id: string) => void
  getTasksByCategory: (category: 'general' | 'study' | 'work') => Task[]
  getTasksByPriority: (priority: 'low' | 'medium' | 'high') => Task[]
  clearAllTasks: () => void
}

export const useTasksStore = create<TasksState>()(
  persist(
    (set, get) => ({
      tasks: [],
      addTask: (taskData) =>
        set((state) => {
          const now = Date.now()
          const task: Task = {
            ...(taskData as Omit<Task, 'id' | 'createdAt' | 'updatedAt'>),
            id: taskData.id || `task-${now}`,
            createdAt: now,
            updatedAt: now,
          }
          const currentTasks = Array.isArray(state.tasks) ? state.tasks : []
          return { tasks: [...currentTasks, task] }
        }),
      updateTask: (id, updates) =>
        set((state) => {
          const currentTasks = Array.isArray(state.tasks) ? state.tasks : []
          return {
            tasks: currentTasks.map((t) =>
              t.id === id ? { ...t, ...updates, updatedAt: Date.now() } : t
            ),
          }
        }),
      deleteTask: (id) =>
        set((state) => {
          const currentTasks = Array.isArray(state.tasks) ? state.tasks : []
          return {
            tasks: currentTasks.filter((t) => t.id !== id),
          }
        }),
      toggleTask: (id) =>
        set((state) => {
          const currentTasks = Array.isArray(state.tasks) ? state.tasks : []
          return {
            tasks: currentTasks.map((t) =>
              t.id === id ? { ...t, done: !t.done, updatedAt: Date.now() } : t
            ),
          }
        }),
      getTasksByCategory: (category) => {
        return get().tasks.filter((t) => t.category === category)
      },
      getTasksByPriority: (priority) => {
        return get().tasks.filter((t) => t.priority === priority)
      },
      clearAllTasks: () => {
        set({ tasks: [] })
      },
    }),
    {
      name: 'tasks',
      storage: createAsyncStorage(),
    }
  )
)

export const initializeTasks = async (): Promise<void> => {
  try {
    // Rehydrate from persisted storage
    await useTasksStore.persist.rehydrate()
    console.log('✅ Tasks rehydrated from storage')
  } catch (error) {
    console.error('❌ Failed to initialize tasks:', error)
  }
}
